/**
 * Design tokens for the **configurator-v3** full-bleed wizard.
 *
 * Ported from the standalone Configurator app's Tailwind `@theme` block
 * (`src/index.css`) + the colour/gradient literals scattered across its
 * components. This is the configurator's counterpart to the area's per-area
 * token blocks (`ACQ`, `PULSE`, `HOME` in `tokens.ts`): the single place raw
 * colour literals / gradients / timings for this wizard may live, so its
 * styled-components carry none.
 *
 * Page-specific literals that only one wizard step uses live in that step's own
 * `*.tokens.ts` (e.g. `steps/design/design.tokens.ts`) re-using the base palette
 * here — this keeps literals out of markup while letting steps be converted in
 * parallel without fighting over one file.
 */

// THEMING — every colour-bearing export below is wrapped through the theming
// registry (`themed()` / `themedValue()`), so each value resolves to a
// `var(--ows-cfg-…)` custom property instead of a hard literal. The literals
// captured here ARE the dark theme (byte-for-byte as before); the light values
// are authored in `themePalette.ts` (LIGHT_OVERRIDES) and emitted by
// `buildThemeCss.ts`. Consumers import the SAME names and need no change —
// light ⇄ dark is then pure CSS (`<html>` carries `data-ows-theme`). This file
// is side-effect-imported by `buildThemeCss.ts` so its vars register before the
// stylesheet builds. Mirrors `customizer-tokens.ts` (the sibling wizard).
//
// EXCEPTION: `CFG_STRIPE` stays a plain literal map — it's handed to Stripe's
// cross-origin iframe, which can't resolve our `var(--ows-…)` custom properties;
// its light variant is selected in React via `useThemeMode()` (see data/payments).
import { themed, themedValue } from './themeRegistry';

// Core palette (Configurator Tailwind @theme → zinc base + brand orange) -------
export const CFG = themed('cfg', {
  // page / surfaces
  pageBg: '#050507',
  card: '#17181c',
  cardAlt: '#1c1c20',
  cardDeep: '#100f13',
  surface: '#151518',
  surfaceRaised: '#1a1a1e',
  forceDark: '#3f3f46',

  // base (zinc) scale
  base50: '#fafafa',
  base100: '#27272a',
  base200: '#e4e4e7',
  base300: '#d4d4d8',
  base400: '#a1a1aa',
  base500: '#71717a',
  base600: '#52525b',
  base800: '#27272a',
  foreground: '#ffffff',

  // text
  text: '#fafafa',
  textMuted: '#a1a1aa',
  textDim: '#d4d4d8',
  textFaint: '#71717a',

  // brand
  brand: '#ff791b',
  brandAlt: '#ff8400',
  brandHover: '#1a130b',
  /** Brand orange used as TEXT/icon. Same orange in dark; on LIGHT it deepens to
   *  a burnt orange that clears AA on white (the raw #ff791b is ~2.6:1 as text) —
   *  use this (not `brand`) for any orange label/eyebrow/link. See themePalette. */
  brandText: '#ff791b',

  // status
  success: '#17c964',
  successAlt: '#00d66f',
  successDeep: '#1aad19',
  warning: '#ffd814',
  danger: '#f04438',
  dangerAlt: '#ff2a2a',

  // lines / washes
  hairline: 'rgba(255, 255, 255, 0.08)',
  hairlineSoft: 'rgba(255, 255, 255, 0.06)',
  hairlineStrong: 'rgba(255, 255, 255, 0.14)',
  wash: 'rgba(255, 255, 255, 0.04)',
  washHover: 'rgba(255, 255, 255, 0.08)',
  scrim: 'rgba(0, 0, 0, 0.5)',
  scrimDeep: 'rgba(0, 0, 0, 0.7)',
} as const);

// Brand gradient (the orange fill used on CTAs / featured accents) ------------
export const CFG_GRADIENTS = themed('cfgGradients', {
  brandFill: 'linear-gradient(180deg, #ff9a4d 0%, #ff791b 42%, rgba(255, 121, 27, 0.25) 100%)',
  // Mobile pack-scan beam (conic sweep around a pill border).
  beam:
    'conic-gradient(transparent 0deg, rgba(255, 0, 212, 0.9) 70deg, rgba(0, 224, 255, 0.95) 130deg, transparent 200deg)',
  // Viewport-background vignette over the 4K texture (v2 parity: centre lets the image show
  // at ~30%, fading to solid black at the edges). Layered above the image url in the Root.
  pageVignette: 'radial-gradient(50% 50% at 50% 50%, rgba(0, 0, 0, 0.7) 0%, rgb(0, 0, 0) 100%)',
} as const);

// Mobile pack-scan beam cover (solid inner disc that leaves only a 1.5px ring).
export const CFG_BEAM_COVER = themedValue('cfgBeamCover', '#141417');

// Wizard chrome (sidebar / footer / progress header / inner cards) — layered
// Figma gradient fills + the chrome's own accents. Ported verbatim from the
// standalone app's `components/wizard/backgrounds.ts` + chrome literals.
export const CFG_CHROME = themed('cfgChrome', {
  /** Sidebar (and the themes-catalog overlay that mirrors it) — solid opaque fill. */
  sidebarBg: '#0f0f13',
  footerBg:
    'linear-gradient(90deg, rgba(13,12,18,0.64) 0%, rgba(13,12,18,0.64) 100%),' +
    'linear-gradient(270deg, rgba(21,20,22,0.128) 0%, rgba(77,77,77,0.008) 100%),' +
    'linear-gradient(90deg, rgba(0,0,0,0.48) 0%, rgba(0,0,0,0.48) 100%),' +
    'linear-gradient(90deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.06) 100%),' +
    'linear-gradient(90deg, rgba(3,3,6,0.82) 0%, rgba(3,3,6,0.82) 100%)',
  /** Docked sidebar header + footer "frosted glass" (Figma Glass effect → CSS).
   *  Both bars share this material. CSS can't render a true refractive material,
   *  so: `dockGlassBg` = the transparentish fill, the blur (Figma **Frost**) + a
   *  `saturate()` for **Refraction** ride on each bar's `backdrop-filter`,
   *  `dockGlassHighlight` = the lit edge (**Light** 80%), `dockGlassSheen` = the
   *  −45° directional light (**Light** angle + **Splay**). Figma Depth/Dispersion
   *  have no CSS twin. */
  dockGlassBg: 'rgba(12, 12, 17, 0.2)',
  dockGlassHighlight: 'rgba(255, 255, 255, 0.12)',
  dockGlassSheen: 'linear-gradient(135deg, rgba(255, 255, 255, 0.07) 0%, rgba(255, 255, 255, 0) 45%)',
  /** Inner section panels (PaneCard: Themes / Color & styling / Promo focus / Billing). */
  cardBg: '#121317',
  /** Sidebar outer border + the docked header/footer separator lines. */
  sidebarBorder: '#27272a',
  trackBg: '#2b2b2d',
  trackShadow: 'inset 0px 1px 1.6px 0px rgba(0, 0, 0, 0.25)',
  switchShadow: '0px 1px 1px rgba(0, 0, 0, 0.05)',
  stepInactive: '#bababe',
  chipBg: '#27272a',
  chipHover: '#33333a',
  check: '#17c964',
  /** Stroke of the completed-step check tick (dark, on the green circle). */
  checkStroke: '#000000',
  dividerVia: 'rgba(255, 255, 255, 0.12)',
  /** Horizontal fade lines flanking the sidebar "or" separator (transparent → dividerVia). */
  dividerFadeLeft: 'linear-gradient(to right, transparent, rgba(255, 255, 255, 0.12))',
  dividerFadeRight: 'linear-gradient(to left, transparent, rgba(255, 255, 255, 0.12))',
  buttonBorder: 'rgba(255, 255, 255, 0.15)',
  /** Text colour of the primary CTA (dark, on the brand-coloured button). */
  ctaText: '#000000',
  // Device-preview screen fills (behind the transparent frame cut-outs).
  previewScreen: '#0c0c10',
  phoneScreen: '#000000',
  /** Brand paint fallback when a skin's API tokens omit a field entirely (not a
   *  design choice, a defensive default; see data/api.ts's toColor()). */
  brandBgFallback: '#1a1a1a',
  brandAccentFallback: '#8a8a8a',
} as const);

// Scrollbar thumb (thin side-pane scrollbar).
export const CFG_SCROLLBAR_THUMB = themedValue('cfgScrollbarThumb', 'rgba(255, 255, 255, 0.14)');

// Feature/player demo-video container fill, applied ONLY on the LIGHT theme. The
// clips are dark-lit 3D renders on a BLACK background and play `object-fit:contain`
// (letterboxed). Filling the rounded video box with solid black keeps it a clean
// black "screen" in light (matching the dark theme) so the letterbox shows black,
// not the light page leaking through. Deliberately NOT theme-wrapped — it must stay
// black even in light mode.
export const CFG_VIDEO_STAGE_LIGHT = '#000000';

// Stripe Elements appearance, themed to the configurator chrome. Passed verbatim
// to Stripe (a third-party theme object rendered in a CROSS-ORIGIN iframe), so —
// unlike every other token here — these MUST stay plain colour LITERALS: the
// iframe cannot resolve our `var(--ows-cfg-…)` custom properties. (That's why
// these are NOT wrapped through themed(), and why the values below are literals
// rather than `CFG.*` references, which now resolve to `var(...)` strings.)
// Ported 1:1 from the v2 Spark integration; `CFG_STRIPE` is the DARK appearance,
// `CFG_STRIPE_LIGHT` its light counterpart — the React payment path picks one by
// the resolved theme via `useThemeMode()` (see data/payments.ts).
export const CFG_STRIPE = {
  // appearance.variables
  primary: '#ff791b', // brand (constant both themes)
  background: '#18181b', // v2 colorBackground + .AccordionItem backgroundColor
  text: '#d4d4d8', // v2 #D4D4D8 (colorText / Secondary / Placeholder)
  danger: '#ff0000', // v2 colorDanger #F00
  success: '#00ff00', // v2 colorSuccess #0F0
  // appearance.rules
  accordionBorder: '#27272a', // v2 .AccordionItem borderColor #27272A
  label: '#a1a1aa', // v2 .Label / .TermsText colour #A1A1AA
  // .Input — matched to the configurator's own email field (FINISH input tokens)
  // so the Stripe Card/Expiry/CVC/Country inputs share its outlined look
  // (transparent surface + 2px border) and 16px size — the latter also stops
  // iOS Safari zooming in when a field is focused (zoom fires below 16px).
  inputBorder: '#27272a', // matches FINISH.inputBorder (#27272a)
  inputBorderFocus: 'rgba(255, 255, 255, 0.4)', // matches FINISH.inputBorderFocus
  inputText: '#ecedee', // matches FINISH.inputText
  inputPlaceholder: '#71717a', // matches FINISH.inputPlaceholder (base500)
} as const;

// Light-theme Stripe appearance — same keys, light values. White field on the
// light pane, dark ink, hairline borders, AA red/green; brand orange stays.
export const CFG_STRIPE_LIGHT = {
  primary: '#ff791b',
  background: '#ffffff',
  text: '#1b1b1f',
  danger: '#c81e2b', // AA red on white (~5.7:1) vs the neon #F00
  success: '#0c7a3c', // AA green on white (~5.4:1) vs the neon #0F0
  accordionBorder: 'rgba(17, 19, 26, 0.12)',
  label: '#5a5f6b',
  inputBorder: 'rgba(17, 19, 26, 0.12)',
  inputBorderFocus: 'rgba(17, 19, 26, 0.45)',
  inputText: '#1b1b1f',
  inputPlaceholder: '#8a8f99',
} as const;

// Typography ------------------------------------------------------------------
export const CFG_FONT = 'Satoshi, ui-sans-serif, system-ui, sans-serif';
/** Monospaced stack for code-like values (e.g. the checkout voucher-code pill). */
export const CFG_MONO_FONT = 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace';

// Responsive — the configurator's own mobile breakpoint (Figma frames 375–390px;
// below 768px the dedicated mobile wizard renders instead of the desktop layout).
export const CFG_MOBILE = '@media (max-width: 767px)';

/** Device preview switch (Mobile / Desktop cross-slide) duration (ms). A module const so
 *  the several places that key off the same number stay in sync: the cross-slide
 *  transition, the ghost-retire timer, and the switch travel offset. (RESIZE_COVER_MS no
 *  longer derives from it — see its note; the cover now tracks the site's reflow, not the
 *  slide's travel.) */
const DEVICE_SWITCH_MS = 1020;

// Animation timings (from the ported keyframes + JS animation loops) ----------
export const CFG_ANIM = {
  PILL_IN_MS: 450,
  PILL_GROW_MS: 700,
  POP_MS: 300,
  /** ConfigPrefs popover (UserMenu) entrance: fade + scale-in from the icon. */
  POPUP_MS: 260,
  BEAM_MS: 1500,
  /** Desktop step change: the main viewport (left preview) slide-in duration (ms).
   *  Mirrors the account pages' PAGE_NAV feel within the wizard's own token set. */
  STEP_SLIDE_MS: 340,
  /** Desktop step change: the main-viewport slide-in travel distance (px) — small
   *  on purpose (a short shared-axis nudge + crossfade, not a full-width push). */
  STEP_SLIDE_OFFSET_PX: 28,
  /** Device preview switch (Mobile / Desktop cross-slide): its own duration (ms),
   *  a slow, deliberate swap (3x the step slide). Kept separate so changing it
   *  never touches the wizard step transition. */
  DEVICE_SWITCH_MS,
  /** Device preview switch: horizontal travel of the incoming/outgoing frame (px).
   *  A long, deliberate slide; overshoot is clipped at the pane edges. */
  DEVICE_SWITCH_OFFSET_PX: 112,
  /** Hover affordance fade (ms) — clickable-card wash + swatch/thumbnail scale. */
  INTERACT_MS: 150,
  /** Device-preview loading cover fade-out once the iframe has loaded (ms) —
   *  mirrors the mobile flow's previewFadeMs so both platforms match. */
  PREVIEW_FADE_MS: 300,
  /** Safety-net backstop (ms) for how long the single-mode morph keeps its loading cover
   *  up after a Mobile / Desktop switch, hiding the cross-origin site's re-layout at the
   *  new viewport width (its homepage carousels re-initialise + re-measure, plainly
   *  visible while it is still stretching from the old width to the new one).
   *
   *  The PRIMARY lift is the framed site's dedicated `page-resized` postMessage, which it
   *  emits once its reflow at the new width has SETTLED — the authoritative "my layout is
   *  done" signal (reflow completion is unobservable across origins, so only the site can
   *  say), distinct from the initial-load `page-rendered`.
   *  This value is just the fallback for a site that never answers, kept well ABOVE a
   *  typical resize reflow so the message wins in the normal case and the cover never
   *  lifts onto a still-stretching page. Decoupled from DEVICE_SWITCH_MS on purpose: the
   *  cover now tracks the site's reflow, not the slide's travel — a slow reflow holds the
   *  cover past the slide (device parked, showing the loading fill) rather than flashing. */
  RESIZE_COVER_MS: 4000,
  /** Highlight reveal: per-character typing speed (ms). */
  TYPE_CHAR_MS: 20,
  /** Soft Apple-like ease used across the wizard. */
  EASE: 'cubic-bezier(0.16, 1, 0.3, 1)',
  /** Device preview switch ease: a much stronger ease-out than EASE (control-point
   *  x values quartered, 0.16/0.3 to 0.04/0.075) so the cross-slide snaps to speed
   *  then decelerates over a very long tail. Stays monotonic (no overshoot). */
  EASE_DEVICE_SWITCH: 'cubic-bezier(0.04, 1, 0.075, 1)',
  EASE_POP: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  /** Lumio open-intro: waves glow rise/fade. */
  EASE_WAVES: 'cubic-bezier(0.25, 1, 0.5, 1)',
  /** Lumio open-intro: hero star hold-then-fly into the header. */
  EASE_HERO_IN: 'cubic-bezier(0.55, 0.05, 0.8, 0.3)',
  /** Card/tile entrance duration (ms) — used when the art-style set is swapped as
   *  the colour changes. Mirrors the acquisition campaign-card entrance. */
  CARD_IN_MS: 440,
  /** Card/tile entrance ease — matches the acquisition campaign card (ACQ.ease). */
  EASE_CARD_IN: 'cubic-bezier(0.22, 1, 0.36, 1)',
} as const;
